import type { LessonTags, TaggedLesson } from './lessonTags';

/**
 * What an edit would cost the capabilities a course builds
 * (docs/PRD-capability-lesson-roleplays.md R5, D2), so every editor asks
 * before it happens: the Structure step, the Content step and Program
 * Settings. Lessons are addressed by module and lesson index; a lesson's
 * Scored Roleplay is on when it builds a capability and its `roleplay` flag
 * is set (the backend keeps that flag in step with the Roleplay toggle).
 */

type Modules = Array<{ lessons: TaggedLesson[] }>;

/** One lesson, by position. */
export interface LessonAt {
  moduleIndex: number;
  lessonIndex: number;
}

const named = (tags: LessonTags, ids: string[]) =>
  tags.capabilities.filter((c) => ids.includes(c.id)).map((c) => ({ id: c.id, name: c.name }));

const isAt = (at: LessonAt[] | LessonAt, mi: number, li: number) =>
  (Array.isArray(at) ? at : [at]).some((a) => a.moduleIndex === mi && a.lessonIndex === li);

/** The capabilities that would have no lesson left if these lessons were removed (or retagged away). */
export const capabilitiesLeftWithoutLesson = (
  modules: Modules,
  tags: LessonTags,
  removed: LessonAt[]
): Array<{ id: string; name: string }> => {
  const before = new Set<string>();
  const after = new Set<string>();
  modules.forEach((m, mi) =>
    m.lessons.forEach((l, li) => {
      if (!l.capabilityId) return;
      before.add(l.capabilityId);
      if (!isAt(removed, mi, li)) after.add(l.capabilityId);
    })
  );
  return named(
    tags,
    Array.from(before).filter((id) => !after.has(id))
  );
};

/** Every lesson of a module, by position: what deleting the module removes. */
export const lessonsOfModule = (modules: Modules, moduleIndex: number): LessonAt[] =>
  (modules[moduleIndex]?.lessons ?? []).map((_, lessonIndex) => ({ moduleIndex, lessonIndex }));

/** The capability whose last lesson this would be, if retagging it to `next` leaves the capability with none. */
export const capabilityLostByRetag = (
  modules: Modules,
  tags: LessonTags,
  at: LessonAt,
  next: { capabilityId: string } | { outcome: string }
): { id: string; name: string } | null => {
  const current = modules[at.moduleIndex]?.lessons[at.lessonIndex]?.capabilityId;
  if (!current || ('capabilityId' in next && next.capabilityId === current)) return null;
  return capabilitiesLeftWithoutLesson(modules, tags, [at]).find((c) => c.id === current) ?? null;
};

/** How many Scored Roleplays each capability has on, given each lesson's flag. */
export const scoredRoleplayCounts = (modules: Modules, flags: boolean[][]): Record<string, number> => {
  const counts: Record<string, number> = {};
  modules.forEach((m, mi) =>
    m.lessons.forEach((l, li) => {
      if (l.capabilityId && flags[mi]?.[li]) counts[l.capabilityId] = (counts[l.capabilityId] ?? 0) + 1;
    })
  );
  return counts;
};

/**
 * The capability whose last Scored Roleplay this is (D2): turning it off
 * leaves the course measuring that capability nowhere. Null when the lesson
 * has no Scored Roleplay on, or the capability has another one.
 */
export const lastScoredRoleplayOf = (
  modules: Modules,
  tags: LessonTags,
  flags: boolean[][],
  at: LessonAt
): { id: string; name: string } | null => {
  const lesson = modules[at.moduleIndex]?.lessons[at.lessonIndex];
  if (!lesson?.capabilityId || !flags[at.moduleIndex]?.[at.lessonIndex]) return null;
  if ((scoredRoleplayCounts(modules, flags)[lesson.capabilityId] ?? 0) > 1) return null;
  return named(tags, [lesson.capabilityId])[0] ?? null;
};

/** Another lesson where the capability's Scored Roleplay is still on, for "still measured in …". */
export const otherScoredRoleplayLesson = (modules: Modules, flags: boolean[][], at: LessonAt): TaggedLesson | null => {
  const capabilityId = modules[at.moduleIndex]?.lessons[at.lessonIndex]?.capabilityId;
  if (!capabilityId) return null;
  for (let mi = 0; mi < modules.length; mi++) {
    for (let li = 0; li < modules[mi].lessons.length; li++) {
      const l = modules[mi].lessons[li];
      if (l.capabilityId === capabilityId && flags[mi]?.[li] && !(mi === at.moduleIndex && li === at.lessonIndex))
        return l;
    }
  }
  return null;
};

/** The confirm text for removing or retagging a capability's last lesson (R5). */
export const lastLessonWarning = (
  capabilities: Array<{ name: string }>,
  what: 'lesson' | 'module' = 'lesson'
): string => {
  const list = capabilities.map((c) => c.name);
  const names = list.length <= 1 ? list[0] ?? '' : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  const one = list.length === 1;
  const opening =
    what === 'module'
      ? `This module holds the last ${one ? 'lesson that builds' : 'lessons that build'} ${names}.`
      : `This is the last lesson that builds ${names}.`;
  return `${opening} Every capability in this course needs a lesson, so you'll need to add one, or remove ${
    one ? 'it' : 'them'
  } from the course, before you can save.`;
};
